import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { DotPattern } from "@tarawa/ui";
import { api } from "@/lib/api";
import { errorMessage, useDriver, type Driver } from "@/lib/driver";
import { Button } from "@/components/ui";

const input =
  "block h-12 w-full rounded-2xl border border-line bg-surface px-4 text-[16px] focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none";

export default function LoginPage() {
  const { driver, setDriver } = useDriver();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => api<{ driver: Driver }>("/auth/login", { method: "POST", body: { email, password } }),
    onSuccess: ({ driver }) => {
      setDriver(driver);
      navigate("/", { replace: true });
    },
  });

  if (driver) return <Navigate to="/" replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-canvas p-4">
      <DotPattern width={22} height={22} className="text-brand-600/15 [mask-image:radial-gradient(420px_circle_at_center,white,transparent)]" />
      <form onSubmit={submit} className="relative w-full max-w-sm space-y-4 rounded-[28px] bg-surface p-7 shadow-xl">
        <div className="mb-2 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="size-11" />
          <div>
            <h1 className="text-xl font-extrabold">Tarawa</h1>
            <p className="text-sm text-muted">Espace livreur</p>
          </div>
        </div>
        <label className="block space-y-1.5 text-sm font-semibold">
          Email
          <input className={input} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="block space-y-1.5 text-sm font-semibold">
          Mot de passe
          <input className={input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {login.error && (
          <p role="alert" className="rounded-xl bg-terracotta-50 px-3 py-2 text-sm font-medium text-terracotta-600">
            {errorMessage(login.error)}
          </p>
        )}
        <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
          Se connecter
        </Button>
      </form>
    </div>
  );
}
